import {
  type SupplierQualityQuestionKey,
  type SupplierQualityResponsesPayload,
  type SupplierQualitySection,
  QA_SECTION_QUESTIONS
} from '@tandemone/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { extractErrorMessage } from '@/api/extract-error'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

import { getSupplierQuality, upsertSupplierQualitySection } from './api'
import { QA_SKIP_CERTS_LABEL } from './enum-labels'
import { FormSkeleton } from './form-skeleton'
import { invalidateOnboarding } from './invalidate-onboarding'
import { PrimaryButton } from './primary-button'
import { SUPPLIER_QUALITY_QUESTION_LABELS } from './quality-labels'
import { StepFooter } from './step-footer'
import { TogglePill } from './toggle-pill'

type AnswerState = {
  answer: boolean | null
  description: string
}

type AnswerMap = Partial<Record<SupplierQualityQuestionKey, AnswerState>>

function hydrate(
  payload: SupplierQualityResponsesPayload,
  sectionKeys: ReadonlyArray<SupplierQualityQuestionKey>
): AnswerMap {
  const map: AnswerMap = {}
  for (const row of payload.responses) {
    if (!sectionKeys.includes(row.questionKey)) continue
    map[row.questionKey] = {
      answer: row.answer,
      description: row.description ?? ''
    }
  }
  return map
}

function emptyAnswer(): AnswerState {
  return { answer: null, description: '' }
}

export function SupplierQualitySectionForm({
  section,
  onSuccess,
  submitLabel = 'Save and continue'
}: {
  section: SupplierQualitySection
  onSuccess: () => void
  submitLabel?: string
}) {
  const queryClient = useQueryClient()
  const [answers, setAnswers] = useState<AnswerMap | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['supplier-quality'],
    queryFn: ({ signal }) => getSupplierQuality(signal)
  })

  const sectionKeys = QA_SECTION_QUESTIONS[section]

  const mutation = useMutation({
    mutationFn: upsertSupplierQualitySection,
    onSuccess: async () => {
      await invalidateOnboarding(queryClient, 'supplier-quality')
      onSuccess()
    },
    onError: (err: unknown) => setSubmitError(extractErrorMessage(err))
  })

  if (isLoading || !data) return <FormSkeleton />

  const current = answers ?? hydrate(data, sectionKeys)
  const skippedByCert = data.skippedByCert

  function readAnswer(key: SupplierQualityQuestionKey): AnswerState {
    return current[key] ?? emptyAnswer()
  }

  function patch(key: SupplierQualityQuestionKey, next: Partial<AnswerState>) {
    setAnswers(prev => {
      const base = prev ?? current
      const existing = base[key] ?? emptyAnswer()
      return { ...base, [key]: { ...existing, ...next } }
    })
    setSubmitError(null)
  }

  const sectionAnswered = sectionKeys.every(
    key => readAnswer(key).answer !== null
  )
  const yesAnswersDescribed = sectionKeys.every(key => {
    const row = readAnswer(key)
    if (row.answer !== true) return true
    return row.description.trim().length > 0
  })
  const canSubmit = sectionAnswered && yesAnswersDescribed

  function handleSubmit() {
    if (!canSubmit || mutation.isPending) return
    const responses = sectionKeys.map(key => {
      const row = readAnswer(key)
      return {
        questionKey: key,
        answer: row.answer as boolean,
        description: row.description.trim() === '' ? null : row.description
      }
    })
    mutation.mutate({ responses })
  }

  return (
    <div className='flex w-full flex-col gap-4'>
      {skippedByCert && (
        <div className='flex items-start justify-between gap-4 rounded-2xl border border-accent bg-accent-light px-6 py-4'>
          <div>
            <p className='text-[14px] font-medium text-ink'>
              Auto-completed by your certification
            </p>
            <p className='mt-1 text-[13px] text-ink-soft'>
              You hold {QA_SKIP_CERTS_LABEL}, so this section is not required.
              You can still fill it in if you want to share more detail with
              buyers.
            </p>
          </div>
          <PrimaryButton type='button' onClick={onSuccess}>
            Done
          </PrimaryButton>
        </div>
      )}

      <section className='rounded-2xl border border-border bg-page p-6'>
        <ul className='flex flex-col gap-5'>
          {sectionKeys.map(key => {
            const row = readAnswer(key)
            const meta = SUPPLIER_QUALITY_QUESTION_LABELS[key]
            const descriptionMissing =
              row.answer === true && row.description.trim().length === 0
            return (
              <li key={key} className='flex flex-col gap-2'>
                <p className='text-[14px] font-medium text-ink'>
                  {meta.question}
                </p>
                <div className='flex items-center gap-2'>
                  <TogglePill
                    className='flex-1 px-4 sm:flex-none'
                    label='Yes'
                    selected={row.answer === true}
                    onClick={() => patch(key, { answer: true })}
                  />
                  <TogglePill
                    className='flex-1 px-4 sm:flex-none'
                    label='No'
                    selected={row.answer === false}
                    onClick={() => patch(key, { answer: false })}
                  />
                </div>
                {row.answer === true && (
                  <div className='flex flex-col gap-1'>
                    <Label
                      htmlFor={`desc-${key}`}
                      className='text-[11px] text-ink-muted'
                    >
                      {meta.describePrompt}{' '}
                      <span className='text-red-500'>*</span>
                    </Label>
                    <Textarea
                      id={`desc-${key}`}
                      rows={2}
                      value={row.description}
                      onChange={event =>
                        patch(key, { description: event.target.value })
                      }
                      aria-invalid={descriptionMissing || undefined}
                    />
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      </section>

      {submitError && <p className='text-sm text-red-600'>{submitError}</p>}

      <StepFooter>
        <PrimaryButton
          type='button'
          disabled={!canSubmit || mutation.isPending}
          onClick={handleSubmit}
        >
          {mutation.isPending ? 'Saving…' : submitLabel}
        </PrimaryButton>
      </StepFooter>
    </div>
  )
}
